<script lang="ts">
  import { ChatWidget, type ChatMessage } from './lib'
  import { createChatHistory } from './lib/stores/chatHistory'
  import {
    requestAssistantResponse,
    setChatMode,
    type ChatMode,
  } from './lib/api/chat'
  import {
    recordSessionStart,
    type SessionStartSource,
  } from './lib/api/analytics'
  import {
    requestSessionToken,
    type SessionTokenLimits,
  } from './lib/api/session'
  import { onDestroy, onMount } from 'svelte'
  import { setApiBase } from './lib/api/config'
  import {
    fetchRuntimeConfig,
    getDefaultRuntimeConfig,
    type RuntimeConfig,
  } from './lib/api/runtime-config'
  import type {
    HomepageCta,
    HomepageConfig,
    WidgetBranding,
    WidgetFeatures,
  } from './lib/types.js'

  const rawApiBase = import.meta.env.VITE_API_BASE
  const fallbackApiBase =
    typeof rawApiBase === 'string' && rawApiBase.trim().length > 0
      ? rawApiBase
      : '/api'

  let {
    embedded = false,
    apiBase = fallbackApiBase,
  }: {
    embedded?: boolean
    apiBase?: string
  } = $props()

  console.info('[widget] init', {
    embedded,
    fallbackApiBase,
    providedApiBase: apiBase,
  })
  let open = $state(false)
  let typing = $state(false)
  const defaultRuntimeConfig = getDefaultRuntimeConfig()
  let currentWelcomeAuthor = $state(defaultRuntimeConfig.welcomeAuthor)
  let currentWelcomeText = $state(defaultRuntimeConfig.welcomeText)
  let widgetBranding = $state<WidgetBranding | null>(
    defaultRuntimeConfig.branding ? { ...defaultRuntimeConfig.branding } : null
  )
  let widgetFeatures = $state<WidgetFeatures>(
    defaultRuntimeConfig.features
      ? { ...defaultRuntimeConfig.features }
      : {
          enableFileUpload: false,
          enableRating: true,
          enableTranscript: true,
          showPoweredBy: true,
        }
  )
  let customCss = $state(defaultRuntimeConfig.branding?.customCss ?? '')
  let composerPlaceholder = $state(
    defaultRuntimeConfig.placeholder || 'Send us a message...'
  )
  type HomepageTheme = 'light' | 'dark'
  const homepageThemeStorageKey = 'chat-homepage-theme'
  let homepageTheme = $state<HomepageTheme>('light')
  function cloneHomepage(config?: HomepageConfig | null): HomepageConfig {
    const fallback: HomepageConfig = defaultRuntimeConfig.homepage ?? {
      heroTitle: 'Your new Chat Widget',
      heroDescription:
        'Open the floating bubble to explore the interactive support experience. Chat already contains knowledge of your website.',
      primaryCta: {
        label: 'Open chat',
        action: 'toggle-chat',
      },
      featureHighlights: [],
      layoutVariant: 'split',
    }

    const source = config ?? fallback
    const hasSecondary = Object.prototype.hasOwnProperty.call(
      source,
      'secondaryCta'
    )
    const secondary =
      source?.secondaryCta && typeof source.secondaryCta === 'object'
        ? {
            ...(fallback.secondaryCta ?? {}),
            ...source.secondaryCta,
          }
        : undefined
    const merged: HomepageConfig = {
      ...fallback,
      ...source,
      primaryCta: {
        ...fallback.primaryCta,
        ...source.primaryCta,
      },
      secondaryCta: secondary,
      featureHighlights:
        source.featureHighlights !== undefined
          ? [...source.featureHighlights]
          : fallback.featureHighlights
            ? [...fallback.featureHighlights]
            : undefined,
    }

    if (!merged.backgroundImage) {
      delete merged.backgroundImage
    }

    if (
      merged.secondaryCta &&
      (!merged.secondaryCta.label ||
        merged.secondaryCta.label.trim().length === 0)
    ) {
      delete merged.secondaryCta
      if (hasSecondary) {
        merged.secondaryCta = null
      }
    } else if (!merged.secondaryCta && hasSecondary) {
      merged.secondaryCta = null
    }

    return merged
  }

  function formatBackgroundImage(image?: string | null) {
    const trimmed = image?.trim()
    if (!trimmed) {
      return '--homepage-image: none'
    }
    return `--homepage-image: url('${trimmed.replace(/'/g, "\\'")}')`
  }

  function isHomepageTheme(value: string | null): value is HomepageTheme {
    return value === 'light' || value === 'dark'
  }

  function restoreHomepageTheme() {
    if (typeof window === 'undefined') return
    try {
      const stored = window.localStorage.getItem(homepageThemeStorageKey)
      if (isHomepageTheme(stored)) {
        homepageTheme = stored
      }
    } catch (error) {
      console.warn('[widget] failed to read homepage theme preference', error)
    }
  }

  function persistHomepageTheme(theme: HomepageTheme) {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(homepageThemeStorageKey, theme)
    } catch (error) {
      console.warn('[widget] failed to persist homepage theme preference', error)
    }
  }

  function toggleHomepageTheme() {
    homepageTheme = homepageTheme === 'light' ? 'dark' : 'light'
    persistHomepageTheme(homepageTheme)
  }

  const defaultHomepage = cloneHomepage(defaultRuntimeConfig.homepage)
  let homepageConfig = $state<HomepageConfig>(defaultHomepage)
  const initialMessages: ChatMessage[] = [
    {
      id: 'welcome',
      role: 'agent',
      author: defaultRuntimeConfig.welcomeAuthor,
      text: defaultRuntimeConfig.welcomeText,
      timestamp: new Date().toISOString(),
    },
  ]
  const history = createChatHistory({
    initialMessages,
    persist: 'local',
  })
  let messages = $state(history.getSnapshot())
  const unsubscribe = history.subscribe((value) => {
    messages = value
  })

  const defaultQuickReplies = [
    'Tell me about features of wooden houses',
    'I want a custom design',
    'How can I contact you?',
  ]

  const defaultQuickReplyResponses = [
    'Our wooden houses use premium Nordic spruce, customizable floor plans, and include full insulation for year-round comfort.',
    'Custom designs start with a short questionnaire from our architects; share your inspiration and we will draft concepts within 48 hours.',
    'You can reach our sales team at support@skyhomes.com or call +1-800-555-0199 weekdays 9am-6pm.',
  ]

  let quickReplies = $state<string[]>(defaultQuickReplies)
  let quickReplyResponseMap: Map<string, string> = new Map()
  applyQuickReplies(defaultQuickReplies, defaultQuickReplyResponses)
  setChatMode(defaultRuntimeConfig.chatMode)

  function applyQuickReplies(messages: string[], responses: string[]) {
    const source = messages.length > 0 ? messages : defaultQuickReplies
    quickReplies = [...source]

    const effectiveResponses =
      responses.length > 0 ? responses : defaultQuickReplyResponses
    const map = new Map<string, string>()
    quickReplies.forEach((text, index) => {
      const candidate = effectiveResponses[index]
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        map.set(text, candidate.trim())
      } else {
        const fallback =
          defaultQuickReplyResponses[index] ?? defaultQuickReplyResponses[0]
        map.set(text, fallback)
      }
    })

    if (map.size === 0) {
      quickReplies.forEach((text, index) => {
        const fallback =
          defaultQuickReplyResponses[index] ?? defaultQuickReplyResponses[0]
        map.set(text, fallback)
      })
    }

    quickReplyResponseMap = map
  }

  const homepageLayout = $derived(
    homepageConfig.layoutVariant === 'centered' ? 'centered' : 'split'
  )

  const homepageBackgroundStyle = $derived(
    formatBackgroundImage(homepageConfig.backgroundImage)
  )

  const homepageHasBackground = $derived(
    Boolean(homepageConfig.backgroundImage?.trim().length)
  )
  const homepageWidgetBackground = $derived(
    homepageTheme === 'light' ? '#f8fafc' : '#0f172a'
  )
  const homepageWidgetSurface = $derived(
    homepageTheme === 'light' ? '#ffffff' : '#111c36'
  )

  function getCtaTarget(cta: HomepageCta | undefined) {
    if (!cta?.href) return undefined
    return /^https?:\/\//i.test(cta.href) ? '_blank' : undefined
  }

  function getCtaRel(target?: string) {
    return target === '_blank' ? 'noreferrer noopener' : undefined
  }

  function updateWelcomeMessage(author: string, text: string) {
    currentWelcomeAuthor = author
    currentWelcomeText = text
    const snapshot = history.getSnapshot()
    if (snapshot.length === 0) return
    const [first, ...rest] = snapshot
    if (first.role !== 'agent') return
    const next: ChatMessage = {
      ...first,
      author,
      text,
    }
    history.set([next, ...rest])
  }

  function applyRuntimeConfig(config: RuntimeConfig) {
    const normalizedAuthor =
      typeof config.welcomeAuthor === 'string' &&
      config.welcomeAuthor.trim().length > 0
        ? config.welcomeAuthor.trim()
        : defaultRuntimeConfig.welcomeAuthor

    const normalizedText =
      typeof config.welcomeText === 'string' &&
      config.welcomeText.trim().length > 0
        ? config.welcomeText.trim()
        : defaultRuntimeConfig.welcomeText

    updateWelcomeMessage(normalizedAuthor, normalizedText)

    applyQuickReplies(
      config.exampleMessages ?? [],
      config.exampleResponses ?? []
    )

    setChatMode(config.chatMode)

    const normalizedPlaceholder =
      typeof config.placeholder === 'string' &&
      config.placeholder.trim().length > 0
        ? config.placeholder.trim()
        : defaultRuntimeConfig.placeholder || 'Send us a message...'
    composerPlaceholder = normalizedPlaceholder

    const brandingDefaults = defaultRuntimeConfig.branding
      ? { ...defaultRuntimeConfig.branding }
      : null
    if (config.branding) {
      widgetBranding = brandingDefaults
        ? { ...brandingDefaults, ...config.branding }
        : { ...config.branding }
    } else {
      widgetBranding = brandingDefaults
    }
    customCss = widgetBranding?.customCss || ''

    // Apply widget features
    const featureDefaults = defaultRuntimeConfig.features
      ? { ...defaultRuntimeConfig.features }
      : {
          enableFileUpload: false,
          enableRating: true,
          enableTranscript: true,
          showPoweredBy: true,
        }
    widgetFeatures = {
      ...featureDefaults,
      ...(config.features ?? {}),
    }

    homepageConfig = cloneHomepage(config.homepage)
  }

  const defaultLimits: SessionTokenLimits = {
    maxMessages: 15,
    maxUserCharacters: 500,
  }

  let sessionToken = $state<string | null>(null)
  let sessionLimits = $state<SessionTokenLimits>(defaultLimits)
  let analyticsSessionId = $state<string | null>(null)
  let sessionStarted = $state(false)
  let sessionStartInFlight: Promise<void> | null = null

  $effect(() => {
    const nextBase = apiBase.trim().length > 0 ? apiBase : fallbackApiBase
    console.info('[widget] set API base', nextBase)
    setApiBase(nextBase)
  })

  async function loadRuntimeConfig() {
    console.info('[widget] fetching runtime config')
    try {
      const config = await fetchRuntimeConfig()
      const merged: RuntimeConfig = {
        ...defaultRuntimeConfig,
        ...config,
        placeholder:
          typeof config.placeholder === 'string' &&
          config.placeholder.trim().length > 0
            ? config.placeholder
            : defaultRuntimeConfig.placeholder,
        exampleMessages: Array.isArray(config.exampleMessages)
          ? config.exampleMessages
          : defaultRuntimeConfig.exampleMessages,
        exampleResponses: Array.isArray(config.exampleResponses)
          ? config.exampleResponses
          : defaultRuntimeConfig.exampleResponses,
        chatMode: config.chatMode === 'chat' ? 'chat' : 'assistant',
        branding: config.branding
          ? defaultRuntimeConfig.branding
            ? { ...defaultRuntimeConfig.branding, ...config.branding }
            : { ...config.branding }
          : defaultRuntimeConfig.branding,
        features: config.features ?? defaultRuntimeConfig.features,
        homepage: config.homepage
          ? {
              ...(defaultRuntimeConfig.homepage ?? {}),
              ...config.homepage,
              primaryCta: {
                ...(defaultRuntimeConfig.homepage?.primaryCta ?? {}),
                ...config.homepage.primaryCta,
              },
              secondaryCta: config.homepage.secondaryCta
                ? {
                    ...(defaultRuntimeConfig.homepage?.secondaryCta ?? {}),
                    ...config.homepage.secondaryCta,
                  }
                : config.homepage.secondaryCta,
              featureHighlights:
                config.homepage.featureHighlights ??
                defaultRuntimeConfig.homepage?.featureHighlights,
            }
          : defaultRuntimeConfig.homepage,
      }
      applyRuntimeConfig(merged)
    } catch (error) {
      console.error('[widget] failed to load runtime config', error)
      applyRuntimeConfig(defaultRuntimeConfig)
    }
  }

  onMount(() => {
    if (!embedded) {
      restoreHomepageTheme()
    }
    loadRuntimeConfig()
    console.info('[widget] mounted', { embedded })
    refreshSession()
  })

  onDestroy(() => {
    unsubscribe()
  })

  $effect(() => {
    history.set(messages)
  })

  async function refreshSession() {
    try {
      console.info('[widget] refreshing session')
      const origin =
        typeof window !== 'undefined' ? window.location.origin : undefined
      const session = await requestSessionToken('simple-session', { origin })
      sessionToken = session.token
      if (!analyticsSessionId) {
        analyticsSessionId = session.token
      }
      sessionLimits = session.limits ?? defaultLimits
      console.info('[widget] session refreshed', {
        limits: sessionLimits,
        hasToken: Boolean(sessionToken),
      })
    } catch (error) {
      console.error('Failed to refresh chat session token', error)
      sessionToken = null
      sessionLimits = defaultLimits
    }
  }

  async function ensureSessionStarted(source: SessionStartSource) {
    if (sessionStarted) return

    if (sessionStartInFlight) {
      try {
        await sessionStartInFlight
      } catch {
        // Ignore errors from a previous attempt so we can retry
      }
      if (sessionStarted) return
    }

    const origin =
      typeof window !== 'undefined' ? window.location.origin : undefined

    const runner = (async () => {
      if (!analyticsSessionId) {
        if (!sessionToken) {
          await refreshSession()
        }
        if (!analyticsSessionId && sessionToken) {
          analyticsSessionId = sessionToken
        }
        if (!analyticsSessionId) {
          const globalCrypto =
            typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
          if (globalCrypto && typeof globalCrypto.randomUUID === 'function') {
            analyticsSessionId = globalCrypto.randomUUID()
          } else {
            analyticsSessionId = `session-${Date.now()}`
          }
        }
      }

      if (!analyticsSessionId) {
        throw new Error('Unable to determine analytics session id')
      }

      await recordSessionStart({
        sessionId: analyticsSessionId,
        source,
        origin,
      })
      sessionStarted = true
    })()

    sessionStartInFlight = runner

    try {
      await runner
    } catch (error) {
      console.error('[widget] failed to record session start', error)
    } finally {
      sessionStartInFlight = null
    }
  }

  async function handleSend(
    event: CustomEvent<{ message: ChatMessage; messages: ChatMessage[] }>
  ) {
    const { message, messages } = event.detail
    const trimmed = message.text.trim()
    const quickReplyResponse = quickReplyResponseMap.get(trimmed)
    if (quickReplyResponse) {
      await ensureSessionStarted('quick_reply')
      console.info('[widget] quick reply used', { text: trimmed })
      history.append({
        id: `agent-${Date.now()}`,
        role: 'agent',
        author: 'AI Assistant',
        text: quickReplyResponse,
        timestamp: new Date().toISOString(),
      })
      return
    }

    typing = true
    console.info('[widget] sending message', {
      messageId: message.id,
      hasSessionToken: Boolean(sessionToken),
    })

    try {
      await ensureSessionStarted('user_message')
      if (!sessionToken) {
        console.info('[widget] session token missing, refreshing before send')
        await refreshSession()
      }

      if (!sessionToken) {
        throw new Error(
          'Assistant temporarily unavailable. Please try again shortly.'
        )
      }

      const response = await requestAssistantResponse(
        {
          messages,
        },
        {
          token: sessionToken,
          analyticsSessionId: analyticsSessionId ?? undefined,
        }
      )

      const assistantMessage = response.message

      history.append({
        ...assistantMessage,
        id: assistantMessage.id ?? `agent-${Date.now()}`,
        role: 'agent',
        author: assistantMessage.author ?? 'AI Assistant',
        timestamp: assistantMessage.timestamp ?? new Date().toISOString(),
      })

      if (response.maxMessagesReached) {
        console.warn('[widget] max messages limit reached for this session')
      }

      console.info('[widget] assistant reply appended', {
        id: assistantMessage.id,
        role: assistantMessage.role,
        maxMessagesReached: response.maxMessagesReached,
      })
    } catch (error) {
      history.append({
        id: `system-${Date.now()}`,
        role: 'system',
        text:
          error instanceof Error
            ? `Something went wrong while contacting the assistant: ${error.message}`
            : 'Something went wrong while contacting the assistant.',
        timestamp: new Date().toISOString(),
      })
      console.error('[widget] send failed', error)
    } finally {
      typing = false
    }
  }
</script>

{#if embedded}
  <ChatWidget
    bind:open
    bind:messages
    {typing}
    title={widgetBranding?.headerTitle || 'Need a hand?'}
    subtitle={widgetBranding?.headerSubtitle ||
      'We typically reply in under 5 minutes.'}
    accentColor={widgetBranding?.primaryColor || '#22d3ee'}
    backgroundColor={widgetBranding?.backgroundColor || '#0f172a'}
    surfaceColor={widgetBranding?.surfaceColor || '#111c36'}
    inputAutoFocus
    placeholder={composerPlaceholder}
    {quickReplies}
    branding={widgetBranding}
    features={widgetFeatures}
    {customCss}
    maxMessages={sessionLimits.maxMessages}
    maxUserCharacters={sessionLimits.maxUserCharacters}
    on:send={handleSend}
  />
{:else}
  <main
    class="demo-page"
    data-layout={homepageLayout}
    data-theme={homepageTheme}
    class:has-background={homepageHasBackground}
    style={homepageBackgroundStyle}
  >
    <button
      class="theme-switcher"
      type="button"
      aria-label={homepageTheme === 'light'
        ? 'Switch to dark mode'
        : 'Switch to light mode'}
      aria-pressed={homepageTheme === 'dark'}
      onclick={toggleHomepageTheme}
    >
      <span class="theme-switcher-label"
        >{homepageTheme === 'light' ? 'Light mode' : 'Dark mode'}</span
      >
      <span class="theme-switcher-track" aria-hidden="true">
        <span class="theme-switcher-thumb"></span>
      </span>
    </button>

    <section class="demo-copy">
      <h1>{homepageConfig.heroTitle}</h1>
      {#if homepageConfig.heroSubtitle}
        <p class="hero-subtitle">{homepageConfig.heroSubtitle}</p>
      {/if}
      {#if homepageConfig.heroDescription}
        <p class="hero-description">{homepageConfig.heroDescription}</p>
      {/if}
      {#if homepageConfig.featureHighlights?.length}
        <ul class="feature-list">
          {#each homepageConfig.featureHighlights as feature, index (index)}
            <li>{feature}</li>
          {/each}
        </ul>
      {/if}
      {#if homepageConfig.primaryCta || homepageConfig.secondaryCta}
        <div class="cta-row">
          {#if homepageConfig.primaryCta.action === 'toggle-chat'}
            <button class="cta" type="button" onclick={() => (open = !open)}>
              {homepageConfig.primaryCta.label}
            </button>
          {:else if homepageConfig.primaryCta.action === 'link' && homepageConfig.primaryCta.href}
            {@const primaryTarget = getCtaTarget(homepageConfig.primaryCta)}
            <a
              class="cta"
              href={homepageConfig.primaryCta.href}
              target={primaryTarget}
              rel={getCtaRel(primaryTarget)}
            >
              {homepageConfig.primaryCta.label}
            </a>
          {/if}

          {#if homepageConfig.secondaryCta}
            {#if homepageConfig.secondaryCta.action === 'toggle-chat'}
              <button
                class="cta secondary"
                type="button"
                onclick={() => (open = !open)}
              >
                {homepageConfig.secondaryCta.label}
              </button>
            {:else if homepageConfig.secondaryCta.action === 'link' && homepageConfig.secondaryCta.href}
              {@const secondaryTarget = getCtaTarget(
                homepageConfig.secondaryCta
              )}
              <a
                class="cta secondary"
                href={homepageConfig.secondaryCta.href}
                target={secondaryTarget}
                rel={getCtaRel(secondaryTarget)}
              >
                {homepageConfig.secondaryCta.label}
              </a>
            {/if}
          {/if}
        </div>
      {/if}
    </section>

    <ChatWidget
      bind:open
      bind:messages
      {typing}
      title={widgetBranding?.headerTitle || 'Need a hand?'}
      subtitle={widgetBranding?.headerSubtitle ||
        'We typically reply in under 5 minutes.'}
      accentColor={widgetBranding?.primaryColor || '#22d3ee'}
      backgroundColor={widgetBranding?.backgroundColor ||
        homepageWidgetBackground}
      surfaceColor={widgetBranding?.surfaceColor || homepageWidgetSurface}
      inputAutoFocus
      placeholder={composerPlaceholder}
      {quickReplies}
      branding={widgetBranding}
      features={widgetFeatures}
      {customCss}
      maxMessages={sessionLimits.maxMessages}
      maxUserCharacters={sessionLimits.maxUserCharacters}
      on:send={handleSend}
    />
  </main>
{/if}

<style>
  .demo-page {
    --homepage-image: none;
    --page-base-background: radial-gradient(
      circle at top,
      #f8fbff 0%,
      #edf3ff 55%,
      #e2ebf8 100%
    );
    --copy-bg: rgba(255, 255, 255, 0.85);
    --copy-border: rgba(71, 85, 105, 0.22);
    --copy-shadow: rgba(15, 23, 42, 0.15);
    --heading-color: #0f172a;
    --subtitle-color: rgba(30, 41, 59, 0.88);
    --text-color: rgba(51, 65, 85, 0.9);
    --feature-bg: rgba(248, 250, 252, 0.92);
    --feature-border: rgba(100, 116, 139, 0.24);
    --feature-text: #1e293b;
    --feature-dot: #0ea5e9;
    --cta-bg: linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%);
    --cta-text: #f8fafc;
    --cta-shadow: rgba(14, 165, 233, 0.38);
    --secondary-text: #334155;
    --secondary-border: rgba(71, 85, 105, 0.3);
    --secondary-hover-border: rgba(51, 65, 85, 0.45);
    --switcher-bg: rgba(255, 255, 255, 0.78);
    --switcher-border: rgba(71, 85, 105, 0.28);
    --switcher-text: #1e293b;
    --switcher-track: #dbeafe;
    --switcher-thumb: #ffffff;
    --switcher-thumb-shadow: rgba(30, 64, 175, 0.2);
    --background-overlay: rgba(15, 23, 42, 0.22);

    position: relative;
    min-height: 100vh;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 2.5rem;
    align-items: center;
    justify-items: center;
    background-image: var(--homepage-image), var(--page-base-background);
    background-repeat: no-repeat, no-repeat;
    background-size: cover, cover;
    background-position: center, center;
    transition:
      background-image 0.3s ease,
      background-color 0.3s ease;
    color-scheme: light;
  }

  .demo-page[data-theme='dark'] {
    --page-base-background: radial-gradient(
      circle at top,
      rgba(15, 23, 42, 0.85),
      #020617 75%
    );
    --copy-bg: rgba(15, 23, 42, 0.75);
    --copy-border: rgba(148, 163, 184, 0.15);
    --copy-shadow: rgba(2, 8, 23, 0.55);
    --heading-color: #f8fafc;
    --subtitle-color: rgba(226, 232, 240, 0.9);
    --text-color: rgba(226, 232, 240, 0.85);
    --feature-bg: rgba(15, 23, 42, 0.35);
    --feature-border: rgba(148, 163, 184, 0.18);
    --feature-text: rgba(226, 232, 240, 0.9);
    --feature-dot: #22d3ee;
    --cta-bg: linear-gradient(135deg, #22d3ee 0%, #818cf8 100%);
    --cta-text: #0f172a;
    --cta-shadow: rgba(34, 211, 238, 0.45);
    --secondary-text: rgba(226, 232, 240, 0.85);
    --secondary-border: rgba(148, 163, 184, 0.25);
    --secondary-hover-border: rgba(148, 163, 184, 0.4);
    --switcher-bg: rgba(15, 23, 42, 0.66);
    --switcher-border: rgba(148, 163, 184, 0.28);
    --switcher-text: rgba(226, 232, 240, 0.9);
    --switcher-track: rgba(51, 65, 85, 0.95);
    --switcher-thumb: #cbd5e1;
    --switcher-thumb-shadow: rgba(2, 8, 23, 0.4);
    --background-overlay: rgba(15, 23, 42, 0.55);
    color-scheme: dark;
  }

  .theme-switcher {
    position: absolute;
    top: 1.5rem;
    right: 1.5rem;
    z-index: 2;
    border: 1px solid var(--switcher-border);
    background: var(--switcher-bg);
    color: var(--switcher-text);
    border-radius: 999px;
    padding: 0.35rem 0.5rem 0.35rem 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 0.65rem;
    backdrop-filter: blur(14px);
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      transform 0.2s ease;
  }

  .theme-switcher:hover {
    transform: translateY(-1px);
  }

  .theme-switcher:focus-visible {
    outline: 2px solid #38bdf8;
    outline-offset: 2px;
  }

  .theme-switcher-label {
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .theme-switcher-track {
    width: 2.5rem;
    height: 1.4rem;
    border-radius: 999px;
    background: var(--switcher-track);
    padding: 0.14rem;
    display: flex;
    align-items: center;
    box-sizing: border-box;
  }

  .theme-switcher-thumb {
    width: 1.12rem;
    height: 1.12rem;
    border-radius: 50%;
    background: var(--switcher-thumb);
    box-shadow: 0 4px 10px var(--switcher-thumb-shadow);
    transition: transform 0.22s ease;
  }

  .demo-page[data-theme='dark'] .theme-switcher-thumb {
    transform: translateX(1.1rem);
  }

  .demo-copy {
    max-width: 420px;
    background: var(--copy-bg);
    padding: 2rem;
    border-radius: 1.5rem;
    border: 1px solid var(--copy-border);
    backdrop-filter: blur(20px);
    box-shadow: 0 25px 60px var(--copy-shadow);
  }

  h1 {
    margin: 0 0 0.8rem;
    font-size: clamp(2.25rem, 4vw, 3rem);
    font-weight: 700;
    line-height: 1.05;
    color: var(--heading-color);
    white-space: pre-line;
  }

  .hero-subtitle {
    margin: 0 0 0.85rem;
    font-size: 1.05rem;
    color: var(--subtitle-color);
    font-weight: 500;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.6;
    color: var(--text-color);
  }

  .cta-row {
    margin-top: 1.8rem;
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  .feature-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1.5rem 0 0;
    padding: 0;
    list-style: none;
  }

  .feature-list li {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.85rem;
    border-radius: 999px;
    background: var(--feature-bg);
    border: 1px solid var(--feature-border);
    color: var(--feature-text);
    font-size: 0.85rem;
    letter-spacing: 0.02em;
  }

  .feature-list li::before {
    content: '•';
    color: var(--feature-dot);
    font-weight: 700;
    font-size: 1.2em;
    line-height: 1;
  }

  .cta {
    border: 0;
    border-radius: 999px;
    padding: 0.75rem 1.4rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    background: var(--cta-bg);
    color: var(--cta-text);
    box-shadow: 0 16px 35px var(--cta-shadow);
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .cta.secondary {
    background: transparent;
    color: var(--secondary-text);
    border: 1px solid var(--secondary-border);
    box-shadow: none;
  }

  .cta:hover {
    transform: translateY(-2px);
  }

  .cta.secondary:hover {
    border-color: var(--secondary-hover-border);
  }

  .demo-page[data-layout='centered'] {
    grid-template-columns: minmax(0, 520px);
    justify-content: center;
    text-align: center;
    padding: 3rem 1.75rem;
  }

  .demo-page[data-layout='centered'] .demo-copy {
    max-width: 520px;
    align-items: center;
  }

  .demo-page[data-layout='centered'] .feature-list {
    justify-content: center;
  }

  .demo-page[data-layout='centered'] .cta-row {
    justify-content: center;
  }

  .demo-page.has-background::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--background-overlay);
    z-index: 0;
  }

  .demo-page.has-background > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 720px) {
    .theme-switcher {
      top: 1rem;
      right: 1rem;
    }

    .demo-copy {
      padding: 1.75rem;
    }
  }
</style>
